import { type SubmitWireInputV2, type SubmitWireOutput, useQueryClient, useSubmitWireV2, type Wire } from '@egw/api'
import { sendEvent } from '@egw/events'
import { Logger } from '@egw/logger'
import { useMemo } from 'react'
import { buildSubmitWireV2Input } from '../utils'

const log = new Logger('useSubmitWire Hook')
interface SubmitWirePropsInterface {
  onSuccess: () => void
  onError: () => void
}

interface SubmitWireReturnInterface {
  submitWire: (_wireData: Wire) => void
  wireSubmissionIsLoading: boolean
}

export const useSubmitWire = ({ onSuccess, onError }: SubmitWirePropsInterface) => {
  const client = useQueryClient()

  const { mutate: submitWireV2, isLoading: isLoadingV2 } = useSubmitWireV2({
    onSuccess: (data: SubmitWireOutput, variables?: SubmitWireInputV2) => {
      if (data.__typename === 'Wire' && data.isSubmitted) {
        sendEvent('user_wireTransfer_accept', {
          eventSource: 'egWealth',
          category: 'wire-transfer',
          subCategory: 'result',
          fromAccount: variables?.accountAlias,
          accountType: 'checking',
          recipient: variables?.to.name,
          memo: variables?.reference,
          amount: variables?.money.amount,
        })
        onSuccess()
        void client.invalidateQueries({
          queryKey: ['bankAccountActivity', { alias: variables?.accountAlias }],
          refetchType: 'all',
        })
      } else if (data.__typename === 'SubmitWireError') {
        sendEvent('user_wireTransfer_reject', {
          eventSource: 'egWealth',
          category: 'wire-transfer',
          subCategory: 'result',
          fromAccount: variables?.accountAlias,
          accountType: 'checking',
          recipient: variables?.to.name,
          memo: variables?.reference,
          amount: variables?.money.amount,
          failureReason: data.errorType,
        })
        onError()
      }
    },
    onError: (e, variables) => {
      sendEvent('user_wireTransfer_error', {
        eventSource: 'egWealth',
        category: 'wire-transfer',
        subCategory: 'result',
        fromAccount: variables?.accountAlias,
        accountType: 'checking',
        recipient: variables?.to.name,
        memo: variables?.reference,
        amount: variables?.money.amount,
        error: e,
      })
      onError()
    },
  })

  const appWealthSubmitWire: SubmitWireReturnInterface = useMemo(
    () => ({
      submitWire: (wireData: Wire) => {
        log.info('Submitting Wire V2')
        void submitWireV2(
          buildSubmitWireV2Input({
            wireData,
          }),
        )
      },
      wireSubmissionIsLoading: isLoadingV2,
    }),
    [isLoadingV2, submitWireV2],
  )

  return appWealthSubmitWire
}
